import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronLeft, FileText } from 'lucide-react';

import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { PlatformBadge } from '../../features/statistics/ui/PlatformBadge';
import { DateRangeSelector } from '../../features/statistics/components/DateRangeSelector';
import { useDateRangeParams } from '../../features/statistics/hooks/useDateRangeParams';
import { ExportMenu } from '../../features/statistics/components/ExportMenu';
import {
  ACCOUNT_REPORTS,
  accountHistoryUrl,
  accountReportUrl,
  backToAccountsUrl,
} from '../../features/statistics/lib/account-links';
import { resolveDateRange } from '../../features/statistics/lib/date-range';
import {
  getTikTokStats,
  getTikTokVideosInRange,
  type TikTokStats,
  type TikTokVideo,
} from '../../shared/api/tiktok.api';

const fmt = (n: number) => n.toLocaleString('ru-RU');
const videoEr = (v: TikTokVideo) =>
  v.viewCount > 0 ? ((v.likeCount + v.commentCount + v.shareCount) / v.viewCount) * 100 : 0;

function Tile({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="text-2xl font-semibold tabular-nums mt-1">{value}</div>
      </CardContent>
    </Card>
  );
}

/**
 * embedded — рендер внутри другой страницы (Dashboard): скрывает кнопку
 * «← Общая статистика», у вмещающей страницы своя навигация.
 */
export function TikTokAccountAnalytics({
  openId,
  embedded = false,
}: {
  openId: string;
  embedded?: boolean;
}): JSX.Element {
  const navigate = useNavigate();
  const { search } = useLocation();
  const [dateRangeOption, setDateRangeOption] = useDateRangeParams();
  const resolvedRange = useMemo(() => resolveDateRange(dateRangeOption), [dateRangeOption]);

  const [stats, setStats] = useState<TikTokStats | null>(null);
  const [videos, setVideos] = useState<TikTokVideo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const from = resolvedRange.from.getTime();
  const to = resolvedRange.to.getTime();

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    (async () => {
      try {
        const [s, v] = await Promise.all([
          getTikTokStats(openId),
          getTikTokVideosInRange(openId, from, to),
        ]);
        if (cancelled) return;
        setStats(s);
        setVideos(v.slice().sort((a, b) => b.createTime - a.createTime));
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [openId, from, to]);

  const totals = videos.reduce(
    (a, v) => ({
      views: a.views + v.viewCount,
      interactions: a.interactions + v.likeCount + v.commentCount + v.shareCount,
    }),
    { views: 0, interactions: 0 },
  );
  const er = totals.views > 0 ? (totals.interactions / totals.views) * 100 : 0;
  const label = stats?.username ? `@${stats.username}` : stats?.displayName || openId;
  const initials = (stats?.username || stats?.displayName || 'TT').replace(/^@/, '').slice(0, 2).toUpperCase();

  return (
    <div className="flex flex-col overflow-auto h-full pb-6">
      {!embedded && (
        <div className="flex items-center gap-2 px-4 pt-4 pb-2">
          <Button
            variant="ghost"
            size="sm"
            className="gap-1.5 -ml-1"
            onClick={() => navigate(backToAccountsUrl(search))}
          >
            <ChevronLeft className="size-4" />
            По аккаунтам
          </Button>
        </div>
      )}

      <div className="flex flex-col gap-5 px-4">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <Avatar className="size-10 shrink-0">
              <AvatarFallback className="text-xs font-semibold">{initials}</AvatarFallback>
            </Avatar>
            <div>
              <div className="font-semibold text-base leading-tight flex items-center gap-2">
                {label}
                <PlatformBadge platform="tiktok" />
              </div>
              {stats?.displayName && (
                <div className="text-sm text-muted-foreground leading-tight">{stats.displayName}</div>
              )}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {/* Истории сборов у TikTok нет — только выгрузка. */}
            {!embedded && (
              <ExportMenu
                label="Выгрузить отчёт"
                variant="outline"
                size="sm"
                formats={ACCOUNT_REPORTS.map((r) => ({ ...r, icon: FileText }))}
                onSelect={(report) => navigate(accountReportUrl(report, `tt:${openId}`, dateRangeOption))}
              />
            )}
            <DateRangeSelector value={dateRangeOption} onChange={setDateRangeOption} />
          </div>
        </div>

        {error ? (
          <div className="flex flex-col items-center justify-center flex-1 gap-2 text-center py-20">
            <p className="text-muted-foreground text-sm">Не удалось загрузить данные</p>
            <p className="text-xs text-muted-foreground/70">{error}</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Tile label="Подписчики" value={stats ? fmt(stats.followerCount) : '—'} />
              <Tile label="Просмотры за период" value={isLoading ? '…' : fmt(totals.views)} />
              <Tile label="Взаимодействия" value={isLoading ? '…' : fmt(totals.interactions)} />
              <Tile label="ER" value={isLoading ? '…' : `${er.toFixed(2)}%`} />
            </div>

            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Видео</TableHead>
                    <TableHead>Дата</TableHead>
                    <TableHead className="text-right">Просмотры</TableHead>
                    <TableHead className="text-right">Лайки</TableHead>
                    <TableHead className="text-right">Комментарии</TableHead>
                    <TableHead className="text-right">Репосты</TableHead>
                    <TableHead className="text-right">ER</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={7} className="h-24 text-center text-muted-foreground">
                        Загрузка…
                      </TableCell>
                    </TableRow>
                  ) : videos.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="h-24 text-center text-muted-foreground">
                        За выбранный период видео не найдено
                      </TableCell>
                    </TableRow>
                  ) : (
                    videos.map((v) => (
                      <TableRow key={v.id}>
                        <TableCell className="max-w-xs">
                          <a
                            href={v.shareUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:underline truncate block"
                          >
                            {v.title || v.id}
                          </a>
                        </TableCell>
                        <TableCell>{new Date(v.createTime * 1000).toLocaleDateString('ru-RU')}</TableCell>
                        <TableCell className="text-right">{fmt(v.viewCount)}</TableCell>
                        <TableCell className="text-right">{fmt(v.likeCount)}</TableCell>
                        <TableCell className="text-right">{fmt(v.commentCount)}</TableCell>
                        <TableCell className="text-right">{fmt(v.shareCount)}</TableCell>
                        <TableCell className="text-right font-semibold">{videoEr(v).toFixed(2)}%</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
